/**
 * Vortex Portals — void portal pairs + tentacle lash defense + Collapse grab/launch.
 *
 * Called every fixed frame from main.ts (after tank physics).
 * Handles: tentacle proximity lash, portal walk-through, portal jump animation,
 * collapse pull/launch sequence.
 */

import type { GameState, VortexHole, Player } from '../types';
import type { GameEffect } from './effects';
import { VORTEX, PHYSICS, KNOCKBACK, TUMBLE, HITLAG, WORLD } from '../constants';
import { enterTumble } from '../physics/tanks';
import { mirrorEnterTumble } from '../physics/transitions';
import { modeIsClassic } from './mode';
import { clearCircle } from '../terrain/grid';
import { spawnSmoothingRegion } from '../terrain/sand-physics';
import { unsettleTanksNearCrater } from '../physics/tanks';
import { cancelGrapple } from './grapple';
import { TANK_VISUAL } from '../constants';

// ─── Helpers ────────────────────────────────────────────────────────────────

function dist(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return Math.sqrt(dx * dx + dy * dy);
}

const msToFrames = (ms: number) => Math.round(ms * 60 / 1000);

// ─── Main per-frame processor ───────────────────────────────────────────────

export function processVortexHoles(state: GameState, fx: GameEffect[]): void {
  const holes = state.vortexHoles;
  if (holes.length === 0 && !state.collapseActive) return;

  // 1. Collapse sequence tick (if active)
  if (state.collapseActive) {
    tickCollapse(state, fx);
  }

  if (holes.length === 0) return;

  // 2. Tentacle lash — proximity defense for portals
  for (const hole of holes) {
    // Tick cooldowns
    for (const key in hole.tentacleCooldowns) {
      if (hole.tentacleCooldowns[key] > 0) {
        hole.tentacleCooldowns[key]--;
      }
    }

    for (let pi = 0; pi < state.players.length; pi++) {
      const p = state.players[pi];
      if (!p.alive) continue;
      if (pi === hole.ownerIndex) continue;
      if (p.portalImmunityFrames > 0) continue;
      if (p.respawnInvulnFrames > 0) continue;
      if (p.collapseGrabbed) continue;

      const d = dist(p.x, p.y, hole.x, hole.y);
      if (d < VORTEX.TENTACLE_RANGE) {
        // Check cooldown for this player on this portal
        const cd = hole.tentacleCooldowns[pi] ?? 0;
        if (cd <= 0) {
          tentacleLash(state, hole, pi, fx);
          hole.tentacleCooldowns[pi] = VORTEX.TENTACLE_COOLDOWN;
        }
      }
    }
  }

  // 3. Passive portal walk-through — Vortex auto-teleports through own portals during aiming
  for (let pi = 0; pi < state.players.length; pi++) {
    const p = state.players[pi];
    if (!p.alive) continue;
    if (p.tankId !== 'vortex') continue;
    if (pi !== state.currentPlayer) continue;
    if (state.phase !== 'aiming') continue;
    if (p.portalJumpUsed) continue;
    if (p.portalJumpFrame >= 0) continue;

    const ownHoles: { hole: VortexHole; idx: number }[] = [];
    for (let i = 0; i < holes.length; i++) {
      if (holes[i].ownerIndex === pi) ownHoles.push({ hole: holes[i], idx: i });
    }
    if (ownHoles.length < 2) continue;

    for (const { hole, idx } of ownHoles) {
      const d = dist(p.x, p.y, hole.x, hole.y);
      if (d < VORTEX.PORTAL_WALK_RADIUS) {
        // Destination = the other portal
        const destHole = ownHoles[0].idx === idx ? ownHoles[1].hole : ownHoles[0].hole;

        p.portalJumpUsed = true;
        p.portalJumpFrame = 0;
        p.portalJumpTargetX = destHole.x;
        p.portalJumpTargetY = destHole.y;
        p.vx = 0;
        p.vy = 0;

        fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [p.x, p.y, 0x9944ff, 20, 0.125] });
        fx.push({ type: 'screen_shake', intensity: 3, decay: 0.85 });
        break;
      }
    }
  }

  // 4. Portal immunity countdown
  for (const p of state.players) {
    if (p.portalImmunityFrames > 0) p.portalImmunityFrames--;
  }

  // 5. Portal jump animation tick
  for (const p of state.players) {
    if (p.portalJumpFrame < 0) continue;
    p.portalJumpFrame++;

    const totalFrames = VORTEX.PORTAL_JUMP_SINK_FRAMES + VORTEX.PORTAL_JUMP_EMERGE_FRAMES;
    if (p.portalJumpFrame <= VORTEX.PORTAL_JUMP_SINK_FRAMES) {
      // Sinking into source portal
    } else if (p.portalJumpFrame <= totalFrames) {
      if (p.portalJumpFrame === VORTEX.PORTAL_JUMP_SINK_FRAMES + 1) {
        p.x = p.portalJumpTargetX;
        p.y = p.portalJumpTargetY;
        p.vx = 0;
        p.vy = 0;
        p.settled = false;
        fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [p.x, p.y, 0x9944ff, 25, 0.15] });
      }
    }

    if (p.portalJumpFrame >= totalFrames) {
      p.portalJumpFrame = -1;
    }
  }

  // 6. Idle portal particles
  for (const hole of holes) {
    if (state.frameCount % 20 === 0) {
      const angle = state.frameCount * 0.08;
      const d = 15 + Math.sin(state.frameCount * 0.04) * 8;
      const px = hole.x + Math.cos(angle) * d;
      const py = hole.y + Math.sin(angle) * d;
      fx.push({ type: 'particles', method: 'spawnExplosion', args: [px, py, 0x6622aa, 3, 0.02] });
    }
  }
}

// ─── Tentacle Lash (proximity trigger) ──────────────────────────────────────

function tentacleLash(state: GameState, portal: VortexHole, targetIndex: number, fx: GameEffect[]): void {
  const target = state.players[targetIndex];
  if (!target.alive) return;

  // Apply damage
  if (!modeIsClassic(state.modeState.mode)) {
    target.damagePercent += VORTEX.TENTACLE_DAMAGE;
  }

  // Knockback — away from portal
  const d = dist(target.x, target.y, portal.x, portal.y);
  let nx = d > 1 ? (target.x - portal.x) / d : 0;
  let ny = d > 1 ? (target.y - portal.y) / d : -1;

  const percentMult = modeIsClassic(state.modeState.mode) ? 1 : 1 + target.damagePercent / KNOCKBACK.KBG;
  const force = VORTEX.TENTACLE_KB * percentMult;

  if (state.grapple.active?.playerIndex === targetIndex) {
    cancelGrapple(state);
  }

  target.vx = nx * force;
  target.vy = ny * force;
  target.settled = false;

  enterTumble(target, {
    hitstunPerSpeed: TUMBLE.HITSTUN_PER_SPEED,
    minHitstun: TUMBLE.MIN_HITSTUN,
    maxHitstun: TUMBLE.MAX_HITSTUN,
    speedGated: true,
    damagePercent: target.damagePercent,
  });
  mirrorEnterTumble(target, 'knockback');

  // Set immunity
  target.portalImmunityFrames = VORTEX.CAPTURE_IMMUNITY_FRAMES;

  // VFX
  fx.push({ type: 'tentacle_lash', portalX: portal.x, portalY: portal.y, targetX: target.x, targetY: target.y });
  fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [target.x, target.y, 0x9944ff, 20, 0.3] });
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [portal.x, portal.y, 0x6622aa, 15, 0.15] });
  fx.push({ type: 'screen_shake', intensity: 5, decay: 0.85 });
  fx.push({ type: 'announce_world', text: 'TENTACLE!', x: target.x, y: target.y - 50, color: 0x9944ff, fontSize: 28 });
  fx.push({ type: 'tank_flash', playerIndex: targetIndex, bright: 0x9944ff, dark: 0x220033, duration: 0.2 });

  // Hitstop
  state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(HITLAG.HITLAG_MS * 0.5));
}

// ─── Portal Pair Placement (called from weapon onImpact) ────────────────────

export function placeVortexPortalPair(state: GameState, impactX: number, impactY: number, ownerIndex: number, fx: GameEffect[]): void {
  const holes = state.vortexHoles;

  // Remove ALL existing portals for this owner (only one pair at a time)
  for (let i = holes.length - 1; i >= 0; i--) {
    if (holes[i].ownerIndex === ownerIndex) {
      fx.push({ type: 'particles', method: 'spawnExplosion', args: [holes[i].x, holes[i].y, 0x6622aa, 15, 0.075] });
      holes.splice(i, 1);
    }
  }

  // Portal 1: at impact location
  holes.push({
    x: impactX,
    y: impactY,
    ownerIndex,
    turnsRemaining: VORTEX.PORTAL_LIFETIME_TURNS,
    tentacleCooldowns: {},
  });

  // Portal 2: behind Vortex's current position
  const owner = state.players[ownerIndex];
  const facingDir = owner.angle > Math.PI / 2 ? 1 : -1; // angle > PI/2 means facing left, so "behind" = right
  const behindX = Math.max(WORLD.WORLD_LEFT + 20, Math.min(WORLD.WORLD_RIGHT - 20, owner.x + facingDir * VORTEX.PORTAL_BEHIND_OFFSET));
  const behindY = owner.y;

  holes.push({
    x: behindX,
    y: behindY,
    ownerIndex,
    turnsRemaining: VORTEX.PORTAL_LIFETIME_TURNS,
    tentacleCooldowns: {},
  });

  // VFX for both portals
  fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [impactX, impactY, 0x9944ff, 30, 0.2] });
  fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [behindX, behindY, 0x9944ff, 25, 0.15] });
  fx.push({ type: 'screen_shake', intensity: 4, decay: 0.85 });
}

// ─── Portal Lifetime Decrement (called from turns.ts) ───────────────────────

export function decrementPortalLifetimes(state: GameState, vortexPlayerIndex: number, fx: GameEffect[]): void {
  const holes = state.vortexHoles;
  for (let i = holes.length - 1; i >= 0; i--) {
    const hole = holes[i];
    if (hole.ownerIndex !== vortexPlayerIndex) continue;
    hole.turnsRemaining--;
    if (hole.turnsRemaining <= 0) {
      fx.push({ type: 'particles', method: 'spawnExplosion', args: [hole.x, hole.y, 0x6622aa, 15, 0.075] });
      holes.splice(i, 1);
    }
  }
}

// ─── Collapse (S2 — activation ability) ─────────────────────────────────────

/**
 * Fire Collapse — tentacles grab enemies near any portal, pull them in, launch upward.
 * Consumes all own portals. Called from phases.ts in the hitscan path.
 */
export function fireCollapse(state: GameState, player: Player, fx: GameEffect[]): void {
  const ownerIndex = state.currentPlayer;
  const holes = state.vortexHoles;
  const ownHoles = holes.filter(h => h.ownerIndex === ownerIndex);

  const hitPlayers = new Set<number>();

  // Store portal positions for rendering before they get consumed
  state.collapsePortals = ownHoles.map(h => ({ x: h.x, y: h.y }));

  // Grab enemies within range of any portal
  for (const hole of ownHoles) {
    for (let pi = 0; pi < state.players.length; pi++) {
      if (pi === ownerIndex) continue;
      if (hitPlayers.has(pi)) continue;
      const target = state.players[pi];
      if (!target.alive) continue;

      const d = dist(target.x, target.y, hole.x, hole.y);
      if (d < VORTEX.COLLAPSE_GRAB_RANGE) {
        hitPlayers.add(pi);

        // Apply damage
        if (!modeIsClassic(state.modeState.mode)) {
          target.damagePercent += VORTEX.COLLAPSE_DAMAGE;
        }

        // Set up pull animation — target gets pulled toward nearest portal
        target.collapseGrabbed = true;
        target.collapseTargetX = hole.x;
        target.collapseTargetY = hole.y;
        target.collapseFrame = 0;
        target.settled = false;

        // Cancel grapple
        if (state.grapple.active?.playerIndex === pi) {
          cancelGrapple(state);
        }

        // Grab VFX
        fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [target.x, target.y, 0x220033, 15, 0.4] });
        fx.push({ type: 'tank_flash', playerIndex: pi, bright: 0xaa44ff, dark: 0x220033, duration: 0.3 });
      }
    }
  }

  // Start collapse sequence
  state.collapseActive = true;
  state.collapseTimer = VORTEX.COLLAPSE_PULL_FRAMES + 15; // pull + launch buffer

  // Big screen effects
  fx.push({ type: 'screen_flash', color: 0x8844ff, alpha: 0.15 });
  fx.push({ type: 'screen_shake', intensity: 10, decay: 0.88 });

  if (hitPlayers.size > 0) {
    fx.push({ type: 'announce_screen', text: 'COLLAPSE!', color: 0xaa44ff, fontSize: 48, hold: 1.2 });
  } else {
    fx.push({ type: 'announce_screen', text: 'COLLAPSE!', color: 0x6622aa, fontSize: 36, hold: 0.8 });
  }

  // Carve terrain at each portal
  for (const hole of ownHoles) {
    const bounds = clearCircle(hole.x, hole.y, VORTEX.COLLAPSE_CARVE_RADIUS);
    if (bounds) {
      fx.push({ type: 'terrain_update', bounds });
      fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [hole.x, hole.y, VORTEX.COLLAPSE_CARVE_RADIUS, 0.5] });
      spawnSmoothingRegion(state, bounds);
      unsettleTanksNearCrater(state.players, hole.x, VORTEX.COLLAPSE_CARVE_RADIUS);
    }
    fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [hole.x, hole.y, 0x8844ff, 35, 0.25] });
  }

  // Consume all own portals
  for (let i = holes.length - 1; i >= 0; i--) {
    if (holes[i].ownerIndex === ownerIndex) {
      holes.splice(i, 1);
    }
  }
}

// ─── Collapse tick (called each frame from processVortexHoles) ──────────────

function tickCollapse(state: GameState, fx: GameEffect[]): void {
  state.collapseTimer--;

  // Pull phase: move grabbed players toward their target portal
  for (let pi = 0; pi < state.players.length; pi++) {
    const p = state.players[pi];
    if (!p.collapseGrabbed) continue;
    if (!p.alive) {
      p.collapseGrabbed = false;
      continue;
    }

    p.collapseFrame++;

    if (p.collapseFrame <= VORTEX.COLLAPSE_PULL_FRAMES) {
      // Lerp toward portal position
      const t = p.collapseFrame / VORTEX.COLLAPSE_PULL_FRAMES;
      const startX = p.x;
      const startY = p.y;
      const dx = p.collapseTargetX - startX;
      const dy = p.collapseTargetY - startY;
      const step = Math.min(1, t * 2); // accelerating pull
      p.x += dx * step * 0.15;
      p.y += dy * step * 0.15;
      p.vx = 0;
      p.vy = 0;
      p.settled = false;

      // Pull trail
      if (p.collapseFrame % 3 === 0) {
        fx.push({ type: 'particles', method: 'spawnExplosion', args: [p.x, p.y, 0x8844ff, 5, 0.04] });
      }
    } else {
      // Launch phase — erupt upward/outward
      const launchAngle = VORTEX.COLLAPSE_LAUNCH_ANGLE;
      // Slight horizontal variation based on player position relative to portal
      const hBias = p.x < p.collapseTargetX ? -0.3 : 0.3;

      const percentMult = modeIsClassic(state.modeState.mode) ? 1 : 1 + p.damagePercent / KNOCKBACK.KBG;
      const speed = VORTEX.COLLAPSE_LAUNCH_SPEED * percentMult;

      p.vx = Math.cos(launchAngle + hBias) * speed;
      p.vy = Math.sin(launchAngle + hBias) * speed;
      p.collapseGrabbed = false;
      p.collapseFrame = 0;
      p.portalImmunityFrames = VORTEX.CAPTURE_IMMUNITY_FRAMES;
      p.settled = false;

      enterTumble(p, {
        hitstunPerSpeed: TUMBLE.HITSTUN_PER_SPEED,
        minHitstun: TUMBLE.MIN_HITSTUN,
        maxHitstun: TUMBLE.MAX_HITSTUN,
        speedGated: true,
        damagePercent: p.damagePercent,
      });
      mirrorEnterTumble(p, 'knockback');

      // Add to firingPhaseKnockedPlayers for DI routing
      if (!state.firingPhaseKnockedPlayers.includes(pi)) {
        state.firingPhaseKnockedPlayers.push(pi);
      }

      // Launch VFX
      fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [p.x, p.y, 0xaa44ff, 35, 0.5] });
      fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [p.x, p.y, 0x220033, 20, 0.6] });
      fx.push({ type: 'screen_shake', intensity: 12, decay: 0.85 });
      fx.push({ type: 'announce_world', text: 'LAUNCHED!', x: p.x, y: p.y - 50, color: 0xaa44ff, fontSize: 36 });

      // Hitstop
      state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(HITLAG.HITLAG_MS));
    }
  }

  // End collapse sequence when timer expires
  if (state.collapseTimer <= 0) {
    state.collapseActive = false;
    // Force-release any still-grabbed players (safety)
    for (const p of state.players) {
      if (p.collapseGrabbed) {
        p.collapseGrabbed = false;
        p.collapseFrame = 0;
        p.vy = -3;
        p.portalImmunityFrames = VORTEX.CAPTURE_IMMUNITY_FRAMES;
      }
    }
  }
}
